import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { ChevronRight } from "lucide-react";
import { isInStock } from "../lib/stock";

const CATEGORIES = [
  { key: "all", label: "Tüm Modeller", desc: "19 model" },
  { key: "Düşük Güç 500W-1KW", label: "Düşük Güç 500W-1KW", desc: "Low-power 500W-1KW" },
  { key: "Düşük Güç 1-2KW", label: "Düşük Güç 1-2KW", desc: "Low-power 1-2KW" },
  { key: "Geleneksel Güç 2-8KW", label: "Geleneksel Güç 2-8KW", desc: "Conventional 2-8KW" },
  { key: "NS Serisi", label: "NS Serisi", desc: "New NS Series" },
  { key: "ND2600 Serisi", label: "ND2600 Serisi", desc: "ND2600 Series" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const active = params.get("kategori") || "all";

  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? products : products.filter((p) => p.category === active)),
    [products, active],
  );

  const counts = useMemo(() => {
    const c = { all: products.length };
    for (const cat of CATEGORIES.slice(1)) c[cat.key] = products.filter((p) => p.category === cat.key).length;
    return c;
  }, [products]);

  const setCat = (key) => {
    if (key === "all") setParams({});
    else setParams({ kategori: key });
  };

  return (
    <div data-testid="products-page">
      <SEO
        title="Elektrikli Motor Kontrolör Modelleri"
        description="Fardriver kontrolör katalog: ND72200, ND72260, ND72300, ND72360, NS72450, NS72680, NS721000, MINI12, ND2600 ve tüm Fardriver modelleri."
        path={`/urunler${active !== "all" ? `?kategori=${active}` : ""}`}
      />
      <PageHero
        overline="Ürün Kataloğu"
        title="FOC Kontrolörler"
        description="Fardriver'ın resmi 19 model 5 seri: Düşük Güç (500W-1KW, 1-2KW), Geleneksel Güç (2-8KW), Yeni NS Serisi ve ND2600. Soldan seriyi seçin."
      />

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3" data-testid="category-sidebar">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Ürün Serileri</p>
              <nav className="border border-gray-200 bg-white">
                {CATEGORIES.map((c) => {
                  const isActive = active === c.key;
                  return (
                    <button
                      key={c.key}
                      onClick={() => setCat(c.key)}
                      data-testid={`sidebar-cat-${c.key}`}
                      className={`w-full text-left flex items-center justify-between px-4 py-3.5 border-b border-gray-100 last:border-b-0 transition-colors ${
                        isActive ? "bg-emerald-50 border-l-2 border-l-[#059669]" : "hover:bg-gray-50 border-l-2 border-l-transparent"
                      }`}
                    >
                      <span className="flex-1">
                        <span className={`block text-sm font-display tracking-tight ${isActive ? "text-[#059669]" : "text-black"}`}>
                          {c.label}
                        </span>
                        <span className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-0.5">{c.desc}</span>
                      </span>
                      <span className="ml-3 text-[10px] font-mono-fd text-gray-400">{counts[c.key] || 0}</span>
                      {isActive && <ChevronRight size={14} className="text-[#059669] ml-2" />}
                    </button>
                  );
                })}
              </nav>

              <div className="mt-6 border border-gray-200 bg-gray-50 p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Yardıma mı ihtiyacınız var?</p>
                <p className="font-display text-sm mt-1">Modeli birlikte seçelim</p>
                <Link to="/iletisim" className="text-xs text-[#059669] font-semibold underline mt-2 inline-block">
                  Danışmanlık →
                </Link>
              </div>
            </div>
          </aside>

          {/* Model list */}
          <div className="lg:col-span-9" data-testid="model-list">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display text-2xl tracking-tighter font-black">
                {CATEGORIES.find((c) => c.key === active)?.label || "Tümü"}
              </h2>
              <p className="text-xs uppercase tracking-[0.18em] text-gray-500">{filtered.length} model</p>
            </div>

            {loading ? (
              <div className="text-center py-20 text-gray-500">Yükleniyor...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-500 border border-dashed border-gray-300">
                Bu kategoride henüz model yok.
              </div>
            ) : (
              <ul className="border border-gray-200 bg-white divide-y divide-gray-100">
                {filtered.map((p, i) => (
                  <motion.li
                    key={p.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.03 }}
                  >
                    <Link
                      to={`/urunler/${p.slug}`}
                      data-testid={`model-row-${p.slug}`}
                      className="flex items-center gap-5 px-4 py-4 hover:bg-emerald-50/50 transition-colors group"
                    >
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-50 border border-gray-200 overflow-hidden flex-shrink-0">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {isInStock(p.slug) ? (
                            <span
                              data-testid={`row-stock-in-${p.slug}`}
                              className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] font-semibold"
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                                style={{ boxShadow: "0 0 6px rgba(16,185,129,0.9)" }}
                              />
                              Stokta Var
                            </span>
                          ) : (
                            <span
                              data-testid={`row-stock-out-${p.slug}`}
                              className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-red-600 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] font-semibold"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                              Stokta Yok
                            </span>
                          )}
                          {p.is_featured && (
                            <span className="text-[9px] uppercase tracking-[0.15em] bg-black text-white px-1.5 py-0.5">Öne Çıkan</span>
                          )}
                        </div>
                        <h3 className="font-display text-base sm:text-lg tracking-tight leading-tight group-hover:text-[#059669]">
                          {p.name}
                        </h3>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-1 sm:line-clamp-2">{p.short_description}</p>
                      </div>
                      <div className="hidden sm:flex flex-col items-end text-right">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Pik Akım</span>
                        <span className="font-display tracking-tight">{p.specs?.["Pik Akım"] || "—"}</span>
                      </div>
                      <ChevronRight size={18} className="text-gray-400 group-hover:text-[#059669] flex-shrink-0" />
                    </Link>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
